import React, {useMemo} from 'react';
import {Container, Grid, Group, Text, useMantineTheme} from '@mantine/core';
import {useMatch} from "react-router";
import {
  AimModules,
  isCompleted,
  ModuleIdentifier,
  TransactionTeam,
  useLazyGetTransactionTeamQuery
} from '@repo/services';
import Attachments from "../../src/aim/Attachments/Attachments";
import Notes from "../../src/aim/Notes/Notes";
import PinnedTabsButton from "../aim/PinnedTabs/PinnedTabsButton";
import {canUpdateTransactionJarvis, errorBoundaryWrapper, useAgLazyQuery, useCurrentUser} from "@repo/utils";
import ChecklistMilestoneDropdown from "./ChecklistMilestoneDropdown";
import DaysRemaining from "../DaysRemaining";
import TransactionRejectOrFailModal from "./TransactionTeam/TransactionRejectOrFailModal";
import AddJarvisIdModal from "./TransactionTeam/AddJarvisIdModal";
import {useMediaQuery} from "@mantine/hooks";
import BackToListButton from "../BackToListButton";
import {useLocation} from "react-router-dom";

/**
 * Props for the AcquisitionHeader component that renders an acquisition transaction's title bar, status badge, remaining days countdown, and action controls.
 *
 * Renders inside an AIM transaction screen and pulls transaction team data via RTK Query to conditionally show a cancel button and load attachments/notes.
 *
 * @param title - The acquisition title to display.
 * @param status - The current status string (e.g. "Draft", "Pending").
 * @param bidDate - The bid date value (passed to DaysRemaining).
 * @param showPinnedTabs - Whether to render the pinned tabs button. Defaults to `false`.
 */
interface AcquisitionHeaderProps {
    title: string;
    status: string;
    bidDate: any;
    showPinnedTabs?: boolean;
}

/**
 * Renders a header bar for an acquisition transaction page, including the title, status badge, countdown to bid date, and action controls backed by the transaction team API.
 *
 * Fetches transaction team details on mount to determine whether a cancel button should appear; wraps all functionality in an error boundary.
 */
const AcquisitionHeader= ({
    title, status, bidDate, showPinnedTabs = false
}: AcquisitionHeaderProps) => {
    const match = useMatch(`/Transactions/:status/:id`);
    const location = useLocation();
    const { userRoles } = useCurrentUser();
    const theme = useMantineTheme();
    const isMd = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
    const isLg = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`);

    const textAlignValues = {
        left: isLg ? 'left' :  'center',
        right: isLg ? 'right' : 'center'
    }
    const justifyValue = isLg ? 'flex-start' :  'center';

    const id = match?.params?.id;

    const [transactionTeamDetails, transactionTeamLoading] =
        useAgLazyQuery<TransactionTeam>(
            id && parseFloat(id) !== 0 ? id : undefined,
            useLazyGetTransactionTeamQuery
        );

    const shouldShowCancelButton = useMemo(
        () =>
            id &&
            id !== '0' &&
            transactionTeamDetails &&
            !transactionTeamDetails?.isCanceled &&
            !isCompleted.includes(transactionTeamDetails?.aimChecklistMilestone?.toLowerCase()),
        [id, transactionTeamDetails, isCompleted]
    );

    const finalStatus = useMemo(() => {
        if (transactionTeamDetails?.isCanceled) {
            return transactionTeamDetails.finalStatus;
        }
        return undefined;
    }, [transactionTeamDetails])

    return (
        <Container
            style={{
                padding: '8px 16px',
                borderBottom: `1px solid #e0e0e0`,
                width: '100%'
            }}
            pl={0}
            pr={0}
            size="responsive"
        >
            {finalStatus && (
              <div style={{ marginBottom: 10, color: 'red' }}>
                  {`This transaction has been canceled (${finalStatus}).`}
              </div>
            )}
            <Grid>
                <Grid.Col span={{ md: 12, lg: 6 }} ta={"left"}>
                    <Group justify={justifyValue}>
                        <ChecklistMilestoneDropdown milestone={status} />
                        <Text fw={100} size={'24px'}>
                            {title}
                        </Text>
                        <DaysRemaining bidDate={bidDate} square={false} />
                    </Group>
                </Grid.Col>
                <Grid.Col span={{ md: 12, lg: 6 }} ta={textAlignValues.right as any}>
                    <div>
                        <BackToListButton />
                        {showPinnedTabs && (
                            <PinnedTabsButton module={AimModules.OP} title={title} />
                        )}
                        <Notes id={match?.params.id || "0"} />
                        <Attachments idType={ModuleIdentifier.aimTransactionTeam} />
                        {/*{canUpdateTxTeamJarvis(userRoles) && (*/}
                            <AddJarvisIdModal transactionId={id} />
                        {/*)}*/}
                        {shouldShowCancelButton && (
                            <TransactionRejectOrFailModal id={id || "0"} />
                        )}
                    </div>
                </Grid.Col>
            </Grid>
        </Container>
    );
};

export default errorBoundaryWrapper(AcquisitionHeader);
